'use client';

import { useState } from 'react';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';

type BillingCycle = 'monthly' | 'yearly';

const PLANS = [
  {
    name: 'Starter',
    planId: null as string | null,
    price: { monthly: 0, yearly: 0 },
    tagline: 'Try Apporb risk-free',
    cta: "Get started — it's free",
    popular: false,
    limits: '1 app · 100 AI calls/mo · 1 team seat',
    features: [
      '1 connected app (iOS or Android)',
      'All store locales',
      '100 AI calls per month',
      'Screenshot editor',
      'Review inbox',
      'Push notification sender',
      'Basic analytics',
      'Community support',
    ],
    notIncluded: [
      'Multiple apps',
      'AI review replies',
      'OrbAI chat assistant',
      'Team seats',
    ],
  },
  {
    name: 'Solo',
    planId: 'solo' as string | null,
    price: { monthly: 29, yearly: 23 },
    tagline: 'For indie developers',
    cta: 'Start free trial',
    popular: true,
    limits: '5 apps · Unlimited locales · 500 AI calls/mo',
    features: [
      '5 connected apps',
      'All store locales (unlimited)',
      '500 AI calls per month',
      'Unlimited screenshots',
      'AI review reply drafts',
      'Push notifications (unlimited)',
      'Full analytics (funnels, velocity)',
      'OrbAI chat assistant',
      'AI Marketing (social posts, scripts)',
      'Email support',
    ],
    notIncluded: [
      'Team seats',
      'Priority support',
    ],
  },
  {
    name: 'Studio',
    planId: 'studio' as string | null,
    price: { monthly: 79, yearly: 63 },
    tagline: 'For small studios',
    cta: 'Start free trial',
    popular: false,
    limits: '20 apps · Unlimited · 2000 AI calls/mo · 3 seats',
    features: [
      '20 connected apps',
      'Unlimited everything',
      '2000 AI calls per month',
      '3 team seats',
      'All Solo features',
      'Usage dashboard',
      'Batch translation across all locales',
      'Priority email support',
    ],
    notIncluded: [],
  },
  {
    name: 'Agency',
    planId: 'agency' as string | null,
    price: { monthly: 199, yearly: 159 },
    tagline: 'For agencies & publishers',
    cta: 'Start free trial',
    popular: false,
    limits: 'Unlimited apps · Unlimited AI · 10 seats',
    features: [
      'Unlimited apps',
      'Unlimited AI calls',
      '10 team seats',
      'All Studio features',
      'White-label option (coming)',
      'API access (coming)',
      'Dedicated support',
      'Custom onboarding',
    ],
    notIncluded: [],
  },
];

function CheckIcon() {
  return <FontAwesomeIcon icon={faCheck} className="w-4 h-4 text-emerald-500 flex-shrink-0" />;
}

function XIcon() {
  return <FontAwesomeIcon icon={faXmark} className="w-4 h-4 text-slate-200 flex-shrink-0" />;
}

function PlanCard({ plan, cycle }: { plan: (typeof PLANS)[0]; cycle: BillingCycle }) {
  const price = plan.price[cycle];
  const annualSaving = plan.price.monthly > 0
    ? Math.round(((plan.price.monthly - plan.price.yearly) / plan.price.monthly) * 100)
    : 0;
  const ctaHref = plan.planId
    ? `/accounts/new?plan=${plan.planId}&cycle=${cycle}`
    : '/accounts/new';

  return (
    <div
      className={`relative flex flex-col rounded-2xl p-6 transition-all ${
        plan.popular
          ? 'shadow-2xl ring-2 ring-[#1d4ed8]/30 scale-[1.02]'
          : 'border border-[#e2e8f0] shadow-sm hover:shadow-lg hover:-translate-y-0.5'
      }`}
      style={
        plan.popular
          ? { background: 'linear-gradient(145deg, #1e40af, #1d4ed8)' }
          : { background: '#ffffff' }
      }
    >
      {plan.popular && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span className="bg-amber-400 text-amber-900 text-xs font-bold px-3 py-1 rounded-full shadow-sm whitespace-nowrap">
            Most Popular
          </span>
        </div>
      )}

      {/* Plan name + tagline */}
      <div className="mb-4">
        <h3 className={`text-lg font-bold ${plan.popular ? 'text-white' : 'text-[#0f172a]'}`}>
          {plan.name}
        </h3>
        <p className={`text-sm mt-0.5 ${plan.popular ? 'text-blue-200' : 'text-[#64748b]'}`}>
          {plan.tagline}
        </p>
      </div>

      {/* Price */}
      <div className="mb-2">
        <div className="flex items-end gap-1">
          <span className={`text-4xl font-extrabold ${plan.popular ? 'text-white' : 'text-[#0f172a]'}`}>
            {price === 0 ? 'Free' : `$${price}`}
          </span>
          {price > 0 && (
            <span className={`text-sm mb-1.5 ${plan.popular ? 'text-blue-200' : 'text-[#94a3b8]'}`}>
              /month
            </span>
          )}
        </div>
        {cycle === 'yearly' && price > 0 && (
          <p className={`text-xs mt-0.5 font-medium ${plan.popular ? 'text-blue-200' : 'text-emerald-600'}`}>
            Save {annualSaving}% · billed annually
          </p>
        )}
        {price === 0 && (
          <p className={`text-xs mt-0.5 ${plan.popular ? 'text-blue-200' : 'text-[#94a3b8]'}`}>
            Free forever
          </p>
        )}
      </div>

      {/* Limits badge */}
      <p className={`text-xs mb-5 font-medium px-2.5 py-1.5 rounded-lg inline-block ${
        plan.popular ? 'bg-white/15 text-blue-100' : 'bg-[#f1f5f9] text-[#475569]'
      }`}>
        {plan.limits}
      </p>

      {/* CTA */}
      <Link
        href={ctaHref}
        className={`w-full text-center py-3 px-4 rounded-xl font-semibold text-sm transition-all mb-6 block ${
          plan.popular
            ? 'bg-white text-[#1d4ed8] hover:bg-blue-50 shadow-md'
            : 'text-white hover:opacity-90 shadow-sm'
        }`}
        style={plan.popular ? undefined : { background: 'linear-gradient(135deg, #1d4ed8, #0284c7)' }}
      >
        {plan.cta}
      </Link>

      {/* Feature list */}
      <ul className="space-y-2.5 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-center gap-2.5 text-sm">
            {plan.popular ? (
              <FontAwesomeIcon icon={faCheck} className="w-4 h-4 text-blue-200 flex-shrink-0" />
            ) : (
              <CheckIcon />
            )}
            <span className={plan.popular ? 'text-blue-50' : 'text-[#334155]'}>{f}</span>
          </li>
        ))}
        {plan.notIncluded.map((f) => (
          <li key={f} className="flex items-center gap-2.5 text-sm opacity-40">
            <XIcon />
            <span className={plan.popular ? 'text-blue-200' : 'text-[#94a3b8]'}>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PricingSection() {
  const [cycle, setCycle] = useState<BillingCycle>('monthly');

  return (
    <section id="pricing" className="py-24" style={{ background: '#f8faff' }}>
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#eff6ff] border border-[#bfdbfe] rounded-full px-4 py-1.5 text-xs font-semibold text-[#1d4ed8]">
            Simple, transparent pricing
          </div>
          <h2 className="text-4xl font-extrabold text-[#0f172a] tracking-tight">
            Start free. Scale when you're ready.
          </h2>
          <p className="text-lg text-[#475569]">
            No setup fees. No hidden costs. Downgrade or cancel any time.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-1 bg-white border border-[#e2e8f0] rounded-xl p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setCycle('monthly')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                cycle === 'monthly'
                  ? 'bg-[#1d4ed8] text-white shadow-sm'
                  : 'text-[#64748b] hover:text-[#1d4ed8]'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setCycle('yearly')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                cycle === 'yearly'
                  ? 'bg-[#1d4ed8] text-white shadow-sm'
                  : 'text-[#64748b] hover:text-[#1d4ed8]'
              }`}
            >
              Yearly
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                cycle === 'yearly' ? 'bg-amber-400 text-amber-900' : 'bg-emerald-100 text-emerald-700'
              }`}>
                -20%
              </span>
            </button>
          </div>
        </div>

        {/* Plan grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
          {PLANS.map((plan) => (
            <PlanCard key={plan.name} plan={plan} cycle={cycle} />
          ))}
        </div>

        {/* Footer note */}
        <p className="text-center text-sm text-[#64748b] mt-10">
          All paid plans include a <strong className="text-[#0f172a]">14-day free trial</strong> · No credit card required to start ·{' '}
          <a href="mailto:contact@apporb.net" className="text-[#1d4ed8] hover:underline">
            Contact us
          </a>{' '}
          for custom pricing
        </p>
      </div>
    </section>
  );
}
